import { Chart, ChartData, Circle, HelpText } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useBotAnalyticsCRUDActions } from '@mm/state/actions/BotAnalyticsCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  selectContactsAmountAnalytics,
  selectContactsByPlatformAggregated,
  selectContactsBySource,
} from '@mm/state/selectors/botAnalyticsSelector';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classnames from 'classnames';
import { min, sum } from 'lodash';
import { Moment } from 'moment';
import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { colors } from '../../../../../styles/colors';
import { getDormatedDateLabel } from '../../../../../utils/dateUtils';
import './ContactsConversationsChart.scss';
import { PrimaryToolTip } from '../../../../../../../../common/components/PrimaryToolTip/PrimaryToolTip';

type Props = {
  startDate: Moment | null;
  endDate: Moment | null;
  dateRangeMode: string | null;
};

export const ContactsConversationsChart = (props: Props) => {
  const { startDate, endDate, dateRangeMode } = props;
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const contactsAmountAnalytics = useSelector(selectContactsAmountAnalytics);
  const contactsByPlatformAggregated = useSelector(
    selectContactsByPlatformAggregated,
  );
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const { totalContacts } = contactsAmountAnalytics;
  const {
    facebook,
    webChat,
    sms,
    instagram = [],
    dates,
    totals,
  } = contactsByPlatformAggregated;
  const [isTotalContactsMode, setIsTotalContactsMode] = useState(true);
  const contactsBySource = useSelector(selectContactsBySource);
  const { openUniversalUpgradeLandmineModal } = useModalActions();
  const {
    customerChatWidget = 0,
    facebookOptIns = 0,
    messengerAd = 0,
    optInPopups = 0,
    shareLinks = 0,
    igWelcomer = 0,
    trigger = 0,
    iceBreaker = 0,
    igCommentGuards = 0,
    igStoryMentions = 0,
    commentGuards = 0,
    landingPage = 0,
    contactsCsv = 0,
    adConfig = 0,
    fbPageWelcomer = 0,
    smsKeyword = 0,
    page = 0,
    unknown = 0,
  } = contactsBySource;
  const { retrieveContactsAmountAnalytics, retrieveContactsByType } =
    useBotAnalyticsCRUDActions();

  const canViewContactsBySource = useSelector(
    combinedFeatureFlagsSelector,
  ).analyticsBotContactsBySource;

  useEffect(() => {
    retrieveContactsAmountAnalytics(
      activeChatbotId,
      startDate?.format('YYYY-MM-DD'),
      endDate?.format('YYYY-MM-DD'),
    );
    retrieveContactsByType(
      activeChatbotId,
      'by_source',
      startDate?.format('YYYY-MM-DD'),
      endDate?.format('YYYY-MM-DD'),
    );
    retrieveContactsByType(
      activeChatbotId,
      'by_platform_aggregated',
      startDate?.format('YYYY-MM-DD'),
      endDate?.format('YYYY-MM-DD'),
    );
  }, [startDate, endDate]);

  const dataBySource = [
    customerChatWidget,
    fbPageWelcomer,
    commentGuards,
    igWelcomer,
    trigger,
    iceBreaker,
    igCommentGuards,
    igStoryMentions,
    shareLinks,
    landingPage,
    facebookOptIns,
    messengerAd + adConfig,
    optInPopups,
    contactsCsv,
    smsKeyword,
    page,
    unknown,
  ];
  const totalsBySource = sum(dataBySource);

  const totalContactsData = {
    labels: getDormatedDateLabel(dates, 'day'),
    data: [totalContacts, facebook, webChat, sms, instagram],
    color: [
      colors.bluePurple,
      colors.blue,
      colors.green,
      colors.orange,
      colors.redLight,
    ],
    title: ['Total', 'FB Messenger', 'Webchat', 'SMS', 'Instagram'],
  };

  const WithInstagramTooltip = ({
    children,
  }: {
    children: ReactNode | string;
  }) => {
    return (
      <PrimaryToolTip useHover anchorElement={children}>
        Data counted since May 20, 2021.
      </PrimaryToolTip>
    );
  };

  const contactsBySourceData: ChartData = {
    labels: [
      'Customer Chat Widget',
      'FB Page Welcomer',
      'Comment Guards',
      'Instagram DM Responder',
      'Trigger',
      'Ice breaker',
      'Instagram Comment Guards',
      'Instagram Story Mentions',
      'Share Links',
      'Landing Pages',
      'Facebook Opt-ins',
      'Click-to-Messenger Ads',
      'Opt-in Popups',
      'CSV Upload',
      'SMS Keyword',
      'SMS Opt-in widget',
      'Unknown',
    ],
    data: [dataBySource],
    color: [colors.skyBlue],
    types: ['horizontalBar'],
    withoutGradient: true,
  };

  const chartData = isTotalContactsMode
    ? totalContactsData
    : contactsBySourceData;

  const minContactsValues = [
    min(totalContacts),
    min(facebook),
    min(webChat),
    min(sms),
    min(instagram),
  ];
  const minContacts = min(minContactsValues) ?? 0;
  const minYAxes = minContacts > 10 ? minContacts - 10 : 0;
  const additionalOpts = {
    yBeginAtZero: !(
      isTotalContactsMode &&
      totalContacts.every(contactCount => contactCount > 0)
    ),
    min: isTotalContactsMode ? minYAxes : undefined,
    showValuesForHorizontalBar: !isTotalContactsMode,
  };

  const tooltipsCallbacks = {
    label: (item: Chart.ChartTooltipItem, data: Chart.ChartData) => {
      const { datasetIndex, value, label: itemLabel } = item;
      const { datasets } = data;

      if (typeof datasetIndex === 'number' && datasets) {
        const idx = datasets.findIndex(obj => obj.label === 'Instagram');

        const { label } = datasets[datasetIndex];
        const base = `${label}: ${item.value}`;

        if (!idx || item.datasetIndex !== idx) {
          return base;
        }

        return `${base} (Data counted since May 20, 2021)`;
      }
      return `${itemLabel}: ${value}`;
    },
  };

  const amountValues = [
    activeFacebookPage?.totalContacts,
    totals.facebook,
    totals.webChat,
    totals.sms,
    totals.instagram,
  ];

  const setContactsPerDayMode = () => {
    if (!canViewContactsBySource) {
      openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['BOT-ANALYTICS-CHART-TAB'],
        feature: 'analyticsBotContactsBySource',
      });
    } else {
      setIsTotalContactsMode(false);
    }
  };

  const toggleContactsBySource = classnames(
    'contacts-conversations-chart__header-title-toggle',
    {
      'contacts-conversations-chart__header-title-toggle--active':
        !isTotalContactsMode,
    },
  );
  const toggleTotalContacts = classnames(
    'contacts-conversations-chart__header-title-toggle',
    {
      'contacts-conversations-chart__header-title-toggle--active':
        isTotalContactsMode,
    },
  );

  const LegendItem = ({
    color,
    title,
    amount,
  }: {
    color: string;
    title: string | JSX.Element | Element;
    amount?: number;
  }) => (
    <>
      <div className="c-chart__legend-item">
        <Circle className="c-chart__legend-indicator" color={color} />
        <HelpText className="c-chart__legend-label" text={title} />
      </div>
      <div className="c-chart__legend-item-amount">{amount}</div>
    </>
  );

  const customLegend = (
    <>
      <div className="contacts-conversations-chart__legend">
        {isTotalContactsMode &&
          chartData?.title?.map((title, i) => (
            <div className="c-chart__legend-item-container" key={title}>
              {title.toLowerCase().includes('instagram') ? (
                <WithInstagramTooltip>
                  <LegendItem
                    color={chartData?.color[i]}
                    title={title}
                    amount={amountValues[i]}
                  />
                </WithInstagramTooltip>
              ) : (
                <LegendItem
                  color={chartData?.color[i]}
                  title={title}
                  amount={amountValues[i]}
                />
              )}
            </div>
          ))}
      </div>
      <div className="contacts-conversations-chart__legend-info">
        <div className="contacts-conversations-chart__legend-date">
          <span className="contacts-conversations-chart__legend-date-show">
            Show:{' '}
          </span>
          {dateRangeMode ? dateRangeMode : 'Custom date range'}
        </div>
        {!isTotalContactsMode && (
          <div className="contacts-by-chart__total-by-source">
            Total: <span>{totalsBySource}</span>
          </div>
        )}
      </div>
    </>
  );
  return (
    <div className="contacts-conversations-chart">
      <div className="contacts-conversations-chart__header">
        <div
          className={toggleTotalContacts}
          onClick={() => setIsTotalContactsMode(true)}
        >
          Total Contacts by Platform
        </div>
        <div className={toggleContactsBySource} onClick={setContactsPerDayMode}>
          Contacts by Source
        </div>
      </div>
      <Chart
        className="contacts-conversations__chart-main"
        data={chartData}
        customLegend={customLegend}
        additionalOpts={additionalOpts}
        tooltipsCallbacks={tooltipsCallbacks}
        type={isTotalContactsMode ? 'line' : 'horizontalBar'}
      />
    </div>
  );
};
